// © Microsoft Corporation. All rights reserved.
// Provides utility functions to get and set data from browser webStorage.

import { tryParseJson } from "../utilities/Parsing";
import { logger } from "@msnews/diagnostics";

/**
 * Base class for localeStorage and sessionStorage abstractions
 */
export class WebStorage {

    /**
     * Check if the web storage is supported
     * @returns {boolean} true if supported
     */
    public get supported(): boolean {
        return !!this.storage;
    }

    /**
     * Local storage object for persistent storage
     * @param storage Storage used.
     */
    constructor(private readonly storage: Storage) {
    }

    /**
     * Get given key's value from web storage
     * @param {string} key - Key name
     * @returns {string} Key value
     */
    public getItem(key: string): string {
        if (this.supported) {
            return this.storage.getItem(key);
        }
    }

    /**
     * Gets the value from local storage using the key.
     * @param {string} key - the storage key to store under
     * @param {T} defaultValue - the default value to return if parsing fails
     * @returns {T} The parsed JSON value
     */
    public getObject<T>(key: string, defaultValue?: T): T {
        const value = this.getItem(key);
        if (value != null) {
            const parsedObj = tryParseJson(value);
            if (parsedObj != null) {
                return <T>parsedObj;
            }
        }

        return defaultValue;
    }

    /**
     * Gets given index location key from web storage
     * @param {string} key - Key name
     * @returns {string} Key value
     */
    public key(index: number): string {
        if (this.supported && index >= 0) {
            return this.storage.key(index);
        }
    }

    /**
     * Sets the given value after stringifying as JSON
     * @param {string} key - the storage key to store under
     * @param {T} value - the value to serialize and store
     */
    public setObject<T>(key: string, value: T): void {
        (value !== undefined)
            ? this.setItem(key, JSON.stringify(value))
            : this.removeItem(key);
    }

    /**
     * Removes the given key from storage and returns the existing value parsed
     * @param {string} key - the key to remove.
     */
    public removeObject<T>(key: string): T {
        const value = this.removeItem(key);
        if (value != null) {
            return <T>tryParseJson(value);
        }
    }

    /**
     * Set given key's value to local storage
     * @param {string} key - Key name
     * @param {string} value - Key value
     */
    public setItem(key: string, value: string): void {
        if (this.supported) {
            // Catch quota exceeded exception
            try {
                this.storage.setItem(key, value);
            } catch (e) {
                logger.logError(e);
            }
        }
    }

    /**
     * Remove key item from local storage
     * @param {string} key - Key name
     * @returns {string} Key value
     */
    public removeItem(key: string): string {
        if (this.supported) {
            const value = this.getItem(key);
            this.storage.removeItem(key);
            return value;
        }
    }

    /**
     * Removes all keys containing the given substring in the storage key
     * @param {string} substring The substring to check
     */
    public removeSubstringKeys(substring: string): void {
        if (!this.supported || !substring) {
            return;
        }

        const matchingKeys = [];
        for (let index = 0; index < this.storage.length; index++) {
            const storageKey = this.key(index);
            if (storageKey && storageKey.includes(substring)) {
                matchingKeys.push(storageKey);
            }
        }

        for (let index = 0; index < matchingKeys.length; index++) {
            this.removeItem(matchingKeys[index]);
        }
    }
}
